import { useCallback } from 'react';
import { Stack } from '@mui/material';
import { extractErrorMessage } from 'app/providers/with-wagmi-auth/utils';
import { packagesModel } from 'entities/mine-package';
import { paymentModel } from 'entities/payment';
import { PaymentType, selectCurrentPaymentAsset, selectEstimatedPayment } from 'entities/payment/model';
import { ConfirmPaymentButtonWithReferralCode } from 'features/payment-actions';
import { Tap2MineReferralInput } from 'features/tap2mine-referral';
import { getToastMessage, TokenAvatar, useAppDispatch, useShallowSelector, useStepper } from 'shared';

import { STEPPER_ROUTES } from '../../../lib/stepper';
import { BackgroundCard, EOAInfo, EOAWarning, PaymentTokenAmount } from '../../shared';

export const PaymentConfirmationCryptoView = () => {
  const dispatch = useAppDispatch();
  const { goBack, next } = useStepper();
  const currentPackage = useShallowSelector(packagesModel.selectCurrentPackage);
  const estimateData = useShallowSelector(selectEstimatedPayment);

  const paymentToken = useShallowSelector(selectCurrentPaymentAsset);

  const amount = paymentToken ? estimateData.amountToPay[paymentToken.tokenId].toString() : '0';

  const handleConfirm = useCallback(
    async (refCode?: string) => {
      if (!paymentToken) return;

      await dispatch(
        paymentModel.thunks.confirmPayment({
          network: paymentToken.tokenNetworkName,
          packageName: currentPackage.name,
          paymentTokenSymbol: paymentToken.symbol,
          paymentType: PaymentType.crypto,
          referralCode: refCode,
        }),
      ).unwrap();
    },
    [currentPackage.name, dispatch, paymentToken],
  );

  const handleBackNavigation = () => {
    dispatch(paymentModel.actions.resetPaymentTokenId());
    goBack();
  };

  if (!paymentToken) return null;

  const {
    tokenNetworkName,
    tokenNetworkImage,
    tokenNetworkShortName,
    image,
    name,
    symbol: tokenSymbol,
    decimals,
  } = paymentToken;

  return (
    <BackgroundCard title="Payment Confirmation" onBack={handleBackNavigation}>
      <Stack alignItems="center" width="100%" spacing={2} mb={2}>
        <TokenAvatar
          size={70}
          tokenImg={image}
          tokenName={name}
          networkName={tokenNetworkName}
          networkImg={tokenNetworkImage}
        />
        <PaymentTokenAmount
          tokenSymbol={tokenSymbol}
          networkName={tokenNetworkShortName}
          amount={amount}
          maxDecimalDigits={decimals}
          decimals={decimals}
          symbol={tokenSymbol}
          withCopy={false}
        />
      </Stack>
      <EOAInfo
        packageName={currentPackage.name}
        network={tokenNetworkName}
        tokenSymbol={tokenSymbol}
        tokenAmount={amount}
        tokenDecimals={decimals}
      />

      <EOAWarning sx={{ mb: 2 }} />

      <Tap2MineReferralInput />

      <ConfirmPaymentButtonWithReferralCode
        confirmAction={handleConfirm}
        onSuccessConfirm={() => next(STEPPER_ROUTES.PAYMENT_CRYPTO_AWAITING)}
        sx={{ mt: 2 }}
      />
    </BackgroundCard>
  );
};
